import { createDebug } from '@shared/debug';
import type { ReactNode } from 'react';
import { AppDownloadLinks } from '../../AppDownloadLinks/AppDownloadLinks';
import { RichContent } from '../../RichContent/RichContent';
import { StaticAction } from '../StaticAction/StaticAction';
import { StaticImage } from '../StaticImage/StaticImage';
import { StaticList } from '../StaticList/StaticList';
import { StaticLookup } from '../StaticLookup/StaticLookup';
import { StaticTable } from '../StaticTable/StaticTable';
import { StaticVideo } from '../StaticVideo/StaticVideo';
import type { StaticContent, StaticLookupRegistry } from '../types';

type StaticContentRendererProps = {
  content: StaticContent;
  lookupData?: StaticLookupRegistry;
};

const debug = createDebug({ isEnabled: false });

/**
 * Discriminated renderer for a single static content value.
 *
 * Strings render as a sanitised {@link RichContent} paragraph. `list` blocks
 * recurse through {@link StaticList}, `action` blocks become {@link StaticAction},
 * `appDownloadLinks` blocks become {@link AppDownloadLinks}, `image` blocks become
 * {@link StaticImage}, `video` blocks become {@link StaticVideo},
 * `lookup` blocks become {@link StaticLookup}, and `table` blocks become
 * {@link StaticTable} (grid or picker variant).
 *
 * @param content - A renderable block from page JSON
 * @param lookupData - Route-owned datasets referenced by lookup blocks
 */
function StaticContentRenderer({ content, lookupData }: StaticContentRendererProps): ReactNode {
  if (typeof content === 'string') {
    return <RichContent tag="p" information={content} className="inline paragraph" data-testid="static-html" />;
  }

  switch (content.type) {
    case 'action':
      return <StaticAction action={content} />;
    case 'appDownloadLinks':
      return <AppDownloadLinks />;
    case 'image':
      return <StaticImage image={content} />;
    case 'list':
      return <StaticList block={content} renderContent={item => <StaticContentRenderer content={item} lookupData={lookupData} />} />;
    case 'lookup':
      return <StaticLookup block={content} dataset={lookupData?.[content.source]} />;
    case 'table':
      return <StaticTable block={content} />;
    case 'video':
      return <StaticVideo video={content} />;
    default:
      debug.forceError(`Unknown static content type: ${(content as { type?: string }).type}`);
      return null;
  }
}

export { StaticContentRenderer, type StaticContentRendererProps };
